import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { RotateCcw, Search, X } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Input } from './input';

export function FilterBar({ children, trailing, className }: { children?: ReactNode; trailing?: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'mb-4 flex flex-wrap items-end gap-x-3 gap-y-3 rounded-xl border border-border/70 bg-card px-4 py-3 shadow-sm',
        className,
      )}
    >
      {children}
      {trailing && <div className="ms-auto flex flex-wrap items-center gap-2 self-center">{trailing}</div>}
    </div>
  );
}

export function FilterField({ label, children, className }: { label?: string; children: ReactNode; className?: string }) {
  if (!label) {
    return (
      <div className={cn('flex min-w-0 flex-col gap-1', className)}>
        <span className="hidden h-[1.125rem] sm:block" aria-hidden />
        <div className="flex h-9 items-center">{children}</div>
      </div>
    );
  }
  return (
    <label className={cn('flex min-w-[9rem] flex-col gap-1', className)}>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export interface SearchFilterBarProps {
  search?: {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
    autoFocus?: boolean;
    label?: string;
    className?: string;
  };
  filters?: ReactNode;
  trailing?: ReactNode;
  onReset?: () => void;
  submitLabel?: string;
  className?: string;
  children?: ReactNode;
}

export function SearchFilterBar({ search, filters, trailing, onReset, submitLabel, className, children }: SearchFilterBarProps) {
  const common = useTranslation('common');
  const [draft, setDraft] = useState(search?.value ?? '');
  useEffect(() => {
    setDraft(search?.value ?? '');
  }, [search?.value]);

  const dirty = search !== undefined && draft.trim() !== search.value;
  const submit = () => {
    if (dirty) search?.onChange(draft.trim());
  };
  const onKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    } else if (event.key === 'Escape' && draft) {
      event.preventDefault();
      setDraft('');
      search?.onChange('');
    }
  };

  return (
    <FilterBar className={className} trailing={trailing}>
      {search && (
        <FilterField label={search.label ?? common.t('action.search')} className={cn('min-w-[14rem] flex-1 sm:max-w-md', search.className)}>
          <div className="relative">
            <Search className="pointer-events-none absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              value={draft}
              autoFocus={search.autoFocus}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onKey}
              placeholder={search.placeholder}
              className="pe-8 ps-8"
            />
            {draft && (
              <button
                type="button"
                aria-label={common.t('action.cancel')}
                onClick={() => {
                  setDraft('');
                  search.onChange('');
                }}
                className="absolute end-1.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-ring"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </div>
        </FilterField>
      )}
      {filters}
      {children}
      {(search || onReset) && (
        <div className="flex items-center gap-2">
          {search && (
            <Button type="button" variant="secondary" onClick={submit} disabled={!dirty}>
              <Search className="h-4 w-4" aria-hidden />
              {submitLabel ?? common.t('action.search')}
            </Button>
          )}
          {onReset && (
            <Button type="button" variant="ghost" onClick={onReset} className="text-muted-foreground">
              <RotateCcw className="h-4 w-4" aria-hidden />
              {common.t('action.reset')}
            </Button>
          )}
        </div>
      )}
    </FilterBar>
  );
}
